import { useState, useEffect, Fragment } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Select } from '../ui/select';
import { Input } from '../ui/input';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const numFilter = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

// trip_date is a DATE string ('YYYY-MM-DD'); avoid new Date() UTC parsing shifting the day
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatDate = (dateStr) => {
  const m = typeof dateStr === 'string' && dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${parseInt(m[2])}/${parseInt(m[3])}/${m[1].slice(2)}`;
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear() % 100}`;
};
const weekday = (dateStr) => {
  const m = typeof dateStr === 'string' && dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateStr);
  return isNaN(d.getTime()) ? '' : WEEKDAYS[d.getDay()];
};

// A rate string is "set" only if it parses to a non-negative number; blank/invalid = no rate
const parseRate = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return isNaN(n) || n < 0 ? null : n;
};

const Billable = ({ user, token, api }) => {
  const [year, setYear] = useState(() => {
    const v = localStorage.getItem('milewise_billable_year');
    return v ? parseInt(v) : new Date().getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const v = localStorage.getItem('milewise_billable_month');
    return v ? parseInt(v) : new Date().getMonth() + 1;
  });
  const [data, setData] = useState(null);
  const [rates, setRates] = useState({});   // customer code -> rate string
  const [error, setError] = useState('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    const fetchBillable = async () => {
      try {
        setError('');
        const result = await api.get(`/trips/summary/billable?year=${year}&month=${month}`, token);
        setData(result);
        // Seed the rate inputs from each customer's saved hourly_rate
        const seeded = {};
        for (const row of result.rows) {
          if (!(row.customer in seeded)) {
            seeded[row.customer] = row.hourly_rate == null ? '' : String(parseFloat(row.hourly_rate));
          }
        }
        setRates(seeded);
      } catch (err) {
        setError(err.message);
      }
    };
    fetchBillable();
  }, [year, month, token, api]);

  // Remember the selected period so a refresh stays on it
  useEffect(() => {
    localStorage.setItem('milewise_billable_year', String(year));
    localStorage.setItem('milewise_billable_month', String(month));
  }, [year, month]);

  const saveRate = async (customer) => {
    try {
      setError('');
      await api.put('/customer-rate', { customer_id: customer, hourly_rate: rates[customer] ?? '' }, token);
    } catch (err) {
      setError(`Could not save rate for ${customer}: ${err.message}`);
    }
  };

  // Group rows by customer (rows arrive already sorted by customer then date)
  const groupByCustomer = (rows) => {
    const groups = [];
    let cur = null;
    for (const row of rows) {
      if (!cur || cur.customer !== row.customer) {
        cur = { customer: row.customer, rows: [] };
        groups.push(cur);
      }
      cur.rows.push(row);
    }
    return groups;
  };

  const groups = data ? groupByCustomer(data.rows) : [];

  // Roll up the three month totals across all customers
  let laborTotal = 0, materialsTotal = 0, billableHours = 0, unbillableHours = 0;
  const missingRate = [];
  for (const group of groups) {
    const hours = group.rows.reduce((a, r) => a + parseFloat(r.billable_time), 0);
    const cost = group.rows.reduce((a, r) => a + parseFloat(r.cost), 0);
    const rate = parseRate(rates[group.customer]);
    billableHours += hours;
    unbillableHours += group.rows.reduce((a, r) => a + parseFloat(r.unbillable_time), 0);
    materialsTotal += cost;
    if (hours > 0 && rate == null) missingRate.push(group.customer);
    if (rate != null) laborTotal += hours * rate;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-4">
        <Select value={year} onChange={e => setYear(e.target.value)} className="w-28">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Select>
        <Select value={month} onChange={e => setMonth(e.target.value)} className="w-40">
          {MONTHS.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
        </Select>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Billable Events - {MONTHS[month]} {year}</CardTitle>
        </CardHeader>
        <CardContent>
          {!data || data.rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No billable events for this period.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pb-2 pl-4">Date</th>
                    <th className="pb-2">Billable (hrs)</th>
                    <th className="pb-2">Unbillable (hrs)</th>
                    <th className="pb-2">Cost</th>
                    <th className="pb-2">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(group => {
                    const hours = group.rows.reduce((a, r) => a + parseFloat(r.billable_time), 0);
                    const cost = group.rows.reduce((a, r) => a + parseFloat(r.cost), 0);
                    const rate = parseRate(rates[group.customer]);
                    return (
                      <Fragment key={group.customer}>
                        <tr className="bg-muted/40 border-t-2 border-border">
                          <td colSpan={5} className="py-2 px-1">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div>
                                <span className="font-medium">{group.customer}</span>
                                <span className="text-muted-foreground font-normal"> · {group.rows.length} event{group.rows.length === 1 ? '' : 's'} · {hours.toFixed(1)} hrs · ${cost.toFixed(2)} materials</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-xs">Rate $/hr</span>
                                <Input
                                  value={rates[group.customer] ?? ''}
                                  onChange={e => setRates(r => ({ ...r, [group.customer]: numFilter(e.target.value) }))}
                                  onBlur={() => saveRate(group.customer)}
                                  className="h-8 w-24"
                                  placeholder="—"
                                />
                                <span className="text-sm">
                                  {rate == null
                                    ? <span className="text-muted-foreground">labor —</span>
                                    : <>labor <span className="font-medium">${(hours * rate).toFixed(2)}</span></>}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                        {group.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-border/50">
                            <td className="py-1 pl-4 whitespace-nowrap">{weekday(row.trip_date)}, {formatDate(row.trip_date)}</td>
                            <td className="py-1">{parseFloat(row.billable_time).toFixed(1)}</td>
                            <td className="py-1">{parseFloat(row.unbillable_time).toFixed(1)}</td>
                            <td className="py-1">${parseFloat(row.cost).toFixed(2)}</td>
                            <td className="py-1 text-muted-foreground">{row.description}</td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>

              <div className="border-t mt-3 pt-3 space-y-1 text-sm">
                <div>Total Billable Time: {billableHours.toFixed(1)} hours</div>
                <div>Total Unbillable Time: {unbillableHours.toFixed(1)} hours</div>
                {missingRate.length > 0 && (
                  <div className="text-destructive">No rate set for: {missingRate.join(', ')} — their labor is not included below.</div>
                )}
                <div className="pt-1 font-semibold">Hourly (labor) Billable: ${laborTotal.toFixed(2)}</div>
                <div className="font-semibold">Materials Billable: ${materialsTotal.toFixed(2)}</div>
                <div className="font-semibold text-primary">Total Billable AR: ${(laborTotal + materialsTotal).toFixed(2)}</div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Billable;
